import { ArrowRight, ArrowUpRight, Check, Mail, MapPin, PackageCheck, Phone, Ruler, Wrench } from "lucide-react";
import React from "react";
import { Link } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { materials, realPhotos, siteConfig } from "@/data/site";

const cityProjects = [
  {
    image: realPhotos.projects.pierSixtyStalls,
    width: 1536,
    height: 2048,
    caption: "Owner-provided project photograph · Pier Sixty",
    kicker: "Partition Stalls project · Chelsea Piers",
    location: "Pier 60 Chelsea Piers, New York City · Manhattan",
    title: "Three stainless-steel stalls and two urinal screens at Pier Sixty.",
    summary:
      "Two owner-provided photographs document an owner-confirmed men’s locker-room project at Pier Sixty with three stainless-steel restroom stalls and two stainless-steel urinal privacy screens. It is a first-hand New York City example for hospitality, event, locker-room, and commercial restroom planning.",
    imageAlt: "Stainless-steel commercial restroom stalls in the men’s locker room at Pier Sixty in Chelsea Piers, New York City",
    facts: [
      { label: "Owner-confirmed material", value: "Stainless steel" },
      { label: "Owner-confirmed setting", value: "Men’s locker room" },
    ],
    disclosure:
      "The project relationship, men’s locker-room setting, three-stall quantity, two-urinal-screen quantity, stainless-steel material, and photograph were provided by Partition Stalls. Exact supply or installation role, manufacturer, finish, date, schedule, outcome, and endorsement are not stated.",
    venueName: "Pier Sixty official website",
    venueUrl: "https://piersixty.com/",
  },
  {
    image: realPhotos.projects.johnsTimesSquare,
    width: 768,
    height: 1024,
    caption: "Owner-provided project photograph · John’s of Times Square",
    kicker: "Partition Stalls project · Midtown Manhattan",
    location: "Times Square, New York City · Midtown Manhattan",
    title: "A custom pizza-color steel corner partition at John’s of Times Square.",
    summary:
      "The owner-provided project photograph documents a custom steel commercial toilet partition assembly fitted to a compact corner restroom at John’s of Times Square. The owner describes the distinctive finish as a custom pizza color; it is not presented as a manufacturer’s official finish name.",
    imageAlt: "Custom pizza-color steel commercial toilet partition at John’s of Times Square in Midtown Manhattan",
    facts: [
      { label: "Owner-confirmed material", value: "Steel" },
      { label: "Photo-observed configuration", value: "Corner partition · overhead-braced" },
    ],
    disclosure:
      "The project relationship, steel material, custom pizza-color description, corner configuration, and photograph were provided by Partition Stalls. Exact compartment quantity, supply or installation role, manufacturer, date, schedule, outcome, and endorsement are not stated.",
    venueName: "John’s of Times Square official website",
    venueUrl: "https://www.johnspizzerianyc.com/",
  },
  {
    image: realPhotos.projects.cineMagicLicPrimary,
    width: 1280,
    height: 1918,
    caption: "Owner-provided project photograph · Cine Magic LIC Studios",
    kicker: "Partition Stalls project · Long Island City",
    location: "Long Island City, New York · New York City",
    title: "Four powder-coated steel stalls at Cine Magic LIC Studios.",
    summary:
      "At Cine Magic LIC Studios on 48th Avenue in Long Island City, an owner-provided project photograph documents four owner-confirmed powder-coated-steel restroom stalls. It is a first-hand New York City project reference for production, studio, and commercial-facility restroom planning.",
    imageAlt: "Powder-coated-steel commercial restroom stall at Cine Magic LIC Studios in Long Island City, New York",
    facts: [
      { label: "Owner-confirmed material", value: "Powder coated steel" },
      { label: "Official venue context", value: "New York City production facility" },
    ],
    disclosure:
      "The project relationship, four-stall quantity, powder-coated-steel material, and photograph were provided by Partition Stalls. Cine Magic LIC Studios’ official website identifies its facility at 3015 48th Avenue in Long Island City, New York as a New York City production facility. Exact supply or installation role, manufacturer, finish name, mounting system, date, schedule, outcome, accessibility scope, and endorsement are not stated.",
    venueName: "Cine Magic LIC Studios official website",
    venueUrl: "https://www.cinemagiclic.com/",
  },
] as const;

const localAreas = [
  {
    name: "Manhattan",
    county: "New York County",
    copy: "For Manhattan commercial restroom work, share the project address, floor, receiving direction, elevator or stair route, staging point, planned work window, drawings, and existing-room photos early. Those project facts help begin with the actual building and room.",
  },
  {
    name: "Brooklyn",
    county: "Kings County",
    copy: "For Brooklyn bathroom partition projects, organize the restroom scope room by room: fixture counts, wall and floor conditions, existing partitions to remove or retain, material direction, mounting preference, and accessory needs. Clear information supports a more useful project-specific review.",
  },
  {
    name: "Queens",
    county: "Queens County",
    copy: "For a Queens commercial restroom, start with the final project address, facility type, restroom location within the building, room measurements, photos, and the route from delivery point to installation area. Confirm those conditions before a partition or accessory scope is finalized.",
  },
  {
    name: "The Bronx",
    county: "Bronx County",
    copy: "For Bronx restroom stall planning, include finished wall and floor conditions, fixture centerlines, door swings, accessibility inputs, and the existing configuration when available. Those details help frame a material, mounting, privacy, and hardware conversation around the room.",
  },
  {
    name: "Staten Island",
    county: "Richmond County",
    copy: "Partition Stalls is based at 243 Grandview Avenue in Staten Island. For a local commercial restroom request, send the project address, clear site photos, plans or measurements, material direction, and the partition and accessory quantities you know so the team can review the actual job.",
  },
  {
    name: "Long Island",
    county: "Nassau and Suffolk County context",
    copy: "Long Island commercial restroom projects are reviewed as part of the broader New York full-service area. The New York project guide includes owner-documented work in Central Islip, Patchogue, and Garden City South; submit the exact site location, drawings or measurements, material direction, and building conditions for the project under review.",
  },
] as const;

export default function NewYorkCity() {
  const canonicalPath = "/service-areas/new-york-city";
  const url = `${siteConfig.url}${canonicalPath}`;
  const quoteHref = "/request-a-quote?serviceArea=new-york";
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
          { "@type": "ListItem", position: 2, name: "Service Areas", item: `${siteConfig.url}/service-areas` },
          { "@type": "ListItem", position: 3, name: "New York", item: `${siteConfig.url}/service-areas/new-york` },
          { "@type": "ListItem", position: 4, name: "New York City", item: url },
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        name: "New York City Commercial Bathroom Partitions and Restroom Stalls",
        description: "Commercial bathroom partitions, restroom stalls, and accessories for New York City and Long Island projects. Compare five materials and request a project review.",
        url,
        isPartOf: { "@id": `${siteConfig.url}/#website` },
        about: { "@id": `${url}#service` },
        breadcrumb: { "@id": `${url}#breadcrumb` },
        primaryImageOfPage: `${siteConfig.url}${cityProjects[0].image.large}`,
        image: cityProjects.map((project) => `${siteConfig.url}${project.image.large}`),
      },
      {
        "@type": "Service",
        "@id": `${url}#service`,
        name: "New York City and Long Island commercial bathroom partition and restroom stall project review",
        serviceType: "Commercial bathroom partitions, toilet stalls, restroom accessories, and project-specific installation review",
        description: "Partition Stalls reviews commercial bathroom partition, toilet stall, and restroom accessory requests for New York City and Long Island projects. Final scope, access, schedule, and installation feasibility are confirmed for each project.",
        provider: { "@id": `${siteConfig.url}/#business` },
        areaServed: [
          { "@type": "City", name: "New York City, New York" },
          ...localAreas.map((area) => ({ "@type": "AdministrativeArea", name: `${area.name}, New York` })),
        ],
        url,
      },
      {
        "@type": "ItemList",
        "@id": `${url}#projects`,
        name: "Owner-confirmed Partition Stalls projects in New York City",
        numberOfItems: cityProjects.length,
        itemListElement: cityProjects.map((project, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "CreativeWork",
            "@id": `${url}#project-${index + 1}`,
            name: project.title,
            description: project.summary,
            image: `${siteConfig.url}${project.image.large}`,
            url: `${url}#nyc-project-${index + 1}`,
            contentLocation: { "@type": "Place", name: project.location },
            publisher: { "@id": `${siteConfig.url}/#business` },
          },
        })),
      },
    ],
  };

  return (
    <>
      <StructuredData data={structuredData} />

      <header className="state-area-hero section-line">
        <div className="state-area-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/service-areas">Service areas</Link><span>/</span><Link href="/service-areas/new-york">New York</Link><span>/</span>New York City</p>
          <span className="kicker">New York City + Long Island · Commercial restroom projects</span>
          <h1>New York City and Long Island commercial bathroom partitions and restroom stalls.</h1>
          <p>Partition Stalls reviews commercial restroom partition, toilet stall, and accessory requests for New York City and Long Island projects. Compare the five material options, share what you know, and start a project-specific installation review. Final scope, building access, schedule, and site conditions are confirmed for each project.</p>
          <div className="button-row">
            <Link href={quoteHref} className="button button--primary">Request a New York City Quote <ArrowUpRight aria-hidden="true" /></Link>
            <a href={`tel:${siteConfig.phoneHref}`} className="button button--outline"><Phone aria-hidden="true" /> Call {siteConfig.phoneDisplay}</a>
          </div>
        </div>
        <figure className="state-area-project__media">
          <picture>
            <source media="(max-width: 640px)" srcSet={cityProjects[0].image.small} />
            <source media="(max-width: 1100px)" srcSet={cityProjects[0].image.medium} />
            <img src={cityProjects[0].image.large} srcSet={`${cityProjects[0].image.small} 640w, ${cityProjects[0].image.medium} 1000w, ${cityProjects[0].image.large} 1600w`} sizes="(max-width: 900px) 100vw, 42vw" alt={cityProjects[0].imageAlt} width={cityProjects[0].width} height={cityProjects[0].height} fetchPriority="high" />
          </picture>
          <figcaption>Owner-provided project photograph · Pier Sixty, Chelsea Piers</figcaption>
        </figure>
      </header>

      <section className="state-area-scope section-line" aria-labelledby="nyc-scope-title">
        <article>
          <Wrench aria-hidden="true" />
          <span className="kicker">Start with the actual building</span>
          <h2 id="nyc-scope-title">A city quote begins with real site information.</h2>
          <p>For a New York City commercial restroom, the project address, borough, floor, delivery point, building access, existing-room photos, plans, and permitted work window can change the planning conversation.</p>
        </article>
        <article>
          <PackageCheck aria-hidden="true" />
          <span className="kicker">One coordinated restroom scope</span>
          <h2>Partitions, mounting, privacy, and accessories.</h2>
          <p>Review restroom stalls with mounting direction, privacy goals, hardware, grab bars, dispensers, mirrors, waste units, and other commercial accessories before the quote is finalized.</p>
          <div className="state-area-scope__links"><Link href="/resources/mounting-styles">Compare mounting systems <ArrowRight aria-hidden="true" /></Link><Link href="/commercial-restroom-accessories">Browse accessories <ArrowRight aria-hidden="true" /></Link></div>
        </article>
      </section>

      <section className="state-area-markets section-line" aria-labelledby="nyc-areas-title">
        <div className="state-area-section-heading">
          <div><span className="kicker">Local project planning</span><h2 id="nyc-areas-title">Five boroughs and Long Island. One project-specific review.</h2></div>
          <p>New York City is made up of five boroughs—Manhattan, Brooklyn, Queens, the Bronx, and Staten Island—and each borough is a New York State county. Long Island work is reviewed through the broader New York full-service coverage. Use the actual project location and conditions to begin the right conversation.</p>
        </div>
        <div className="state-area-market-grid" aria-label="New York City borough and Long Island commercial restroom planning">
          {localAreas.map((area, index) => (
            <article key={area.name}>
              <div><span>0{index + 1}</span><MapPin aria-hidden="true" /></div>
              <h3>{area.name}</h3>
              <p className="state-area-market-grid__mix">{area.county}</p>
              <p>{area.copy}</p>
              {area.name === "Long Island" ? <Link className="text-link" href="/service-areas/new-york">View New York project guide <ArrowRight aria-hidden="true" /></Link> : null}
            </article>
          ))}
        </div>
      </section>

      <section className="state-area-related section-line" aria-labelledby="nyc-material-title">
        <div><span className="kicker">Commercial restroom stalls</span><h2 id="nyc-material-title">Compare five partition materials.</h2></div>
        <nav aria-label="New York City commercial bathroom partition materials">
          {materials.map((material) => <Link key={material.slug} href={`/bathroom-partitions/${material.slug}`}><span>Material guide</span>{material.name} <ArrowRight aria-hidden="true" /></Link>)}
        </nav>
      </section>

      {cityProjects.map((project, index) => (
        <section key={project.title} className="state-area-project section-line" aria-labelledby={`nyc-project-${index + 1}`}>
          <figure className="state-area-project__media">
            <picture>
              <source media="(max-width: 640px)" srcSet={project.image.small} />
              <source media="(max-width: 1100px)" srcSet={project.image.medium} />
              <img src={project.image.large} srcSet={`${project.image.small} 640w, ${project.image.medium} 1000w, ${project.image.large} 1600w`} sizes="(max-width: 900px) 100vw, 58vw" alt={project.imageAlt} width={project.width} height={project.height} loading="lazy" decoding="async" />
            </picture>
            <figcaption>{project.caption}</figcaption>
          </figure>
          <article className="state-area-project__details">
            <span className="kicker">{project.kicker}</span>
            <p className="state-area-project__location"><MapPin aria-hidden="true" /> {project.location}</p>
            <h2 id={`nyc-project-${index + 1}`}>{project.title}</h2>
            <p className="state-area-project__summary">{project.summary}</p>
            <dl className="state-area-project__facts">
              {project.facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
            </dl>
            <p className="state-area-project__disclosure">{project.disclosure}</p>
            <div className="state-area-project__actions">
              <Link href="/service-areas/new-york" className="button button--primary">View New York project guide <ArrowUpRight aria-hidden="true" /></Link>
              <a href={project.venueUrl} target="_blank" rel="noreferrer" className="text-link">{project.venueName} <ArrowUpRight aria-hidden="true" /></a>
            </div>
          </article>
        </section>
      ))}

      <section className="state-area-quote-kit section-line" aria-labelledby="nyc-quote-title">
        <div className="state-area-quote-kit__intro"><Ruler aria-hidden="true" /><span className="kicker kicker--light">New York City quote preparation</span><h2 id="nyc-quote-title">Send the details that change the plan.</h2><p>Share the project location, drawings or measurements, material direction, fixture and accessory scope, delivery route, building access, and intended schedule. The team will review the request around the actual project.</p><div><a href={`mailto:${siteConfig.email}`}><Mail aria-hidden="true" /> {siteConfig.email}</a><a href={`tel:${siteConfig.phoneHref}`}><Phone aria-hidden="true" /> {siteConfig.phoneDisplay}</a></div></div>
        <ol>
          {["New York City project address and facility type", "Plans, partition elevations, or clear field measurements", "Material, mounting, hardware, and privacy direction", "Fixture counts plus commercial restroom accessories", "Loading, elevator, storage, phasing, and site-hour information"].map((item, index) => <li key={item}><span>0{index + 1}</span><p>{item}</p><Check aria-hidden="true" /></li>)}
        </ol>
      </section>

      <aside className="state-area-sources section-line" aria-labelledby="nyc-next-title">
        <div><span className="kicker">Continue planning</span><h2 id="nyc-next-title">Useful next steps for a New York City restroom.</h2><p>Use the material and planning guides to organize the project before sending a quote request. Final requirements depend on the selected system and approved project documents.</p></div>
        <div>
          <Link href="/bathroom-partitions"><span>Materials</span><strong>Compare commercial bathroom partitions</strong><p>Review all five material families before narrowing a direction.</p><ArrowUpRight aria-hidden="true" /></Link>
          <Link href="/resources/privacy-options"><span>Privacy</span><strong>Review commercial privacy options</strong><p>Use clear planning comparisons for panel height, sightlines, and under-panel openings.</p><ArrowUpRight aria-hidden="true" /></Link>
        </div>
      </aside>

      <QuoteBand quoteHref={quoteHref} title="Request a New York City or Long Island commercial bathroom partition quote." copy="Send the project location, plans or measurements, material direction, quantities, accessory scope, schedule, and site conditions. Call or submit the form to begin a project-specific review." />
    </>
  );
}
